import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../../../library/elements/d-menu.js';
import '../../../../library/editors/elements/d-select-checkbox.js';
import '../d-infosec-view-header.js';
import '../../../../library/elements/d-wrap.js';
import '../../../../library/elements/d-step-menu.js';
import './d-data-items.js';
import '../../../../library/editors/components/d-select-add.js';
import './d-edit-computers-network.js';
import { backupFrequencyOptions, noBackupReasonOptions } from 'src/pages/computers-page/infosec-procedure/defaults.js';
import type { BackupChange } from './backup-change';
import type { InfosecDataItem } from '../infosecDataItem';
import type { SelectDropdownOption } from '../../../../library/editors/elements/select-dropdown-option';
import { t } from 'src/i18n';

/**
 *
 */

@customElement('d-edit-backup')
export class DEditBackup extends LitElement {
  static readonly styles = [
    css`
      :host {
        display: block;
      }
    `,
  ];

  @property({ type: Object })
  dataItem!: InfosecDataItem;
  @property({ type: Array })
  partners: SelectDropdownOption[] = [];
  @property({ type: String })
  organizationName = '';

  private static get frequencyOptions() {
    return [
      {
        value: 'never',
        text: t('computers.backupFrequencyNever'),
      },
      {
        value: 'weeklyOrLess',
        text: t('computers.backupFrequencyWeeklyOrLess'),
      },
      {
        value: 'lessThanDaily',
        text: t('computers.backupFrequencyLessThanDaily'),
      },
      {
        value: 'daily',
        text: t('computers.backupFrequencyDaily'),
      },
      {
        value: 'moreThanDaily',
        text: t('computers.backupFrequencyMoreThanDaily'),
      },
      {
        value: 'continuously',
        text: t('computers.backupFrequencyContinuously'),
      },
    ];
  }

  private get responsibleOptions() {
    return [
      {
        value: 'internal',
        text: this.organizationName,
      },
      ...this.partners,
    ];
  }

  _frequencyChanged(value) {
    this.dataItem = {
      ...this.dataItem,
      backupFrequency: value,
    };
    this._dispatchDataItemChanged();
    this.dispatchEvent(
      new CustomEvent<BackupChange>('backup-change', {
        composed: true,
        bubbles: true,
        detail: {
          uuid: this.dataItem.uuid,
          backupFrequency: this.dataItem.backupFrequency,
          backupResponsible: this.dataItem.backupResponsible,
          noBackupReason: this.dataItem.noBackupReason,
        },
      }),
    );
  }

  _responsibleChanged(value) {
    this.dataItem = {
      ...this.dataItem,
      backupResponsible: value,
    };
    this._dispatchDataItemChanged();
    this.dispatchEvent(
      new CustomEvent<BackupChange>('backup-change', {
        composed: true,
        bubbles: true,
        detail: {
          uuid: this.dataItem.uuid,
          backupFrequency: this.dataItem.backupFrequency,
          backupResponsible: this.dataItem.backupResponsible,
          noBackupReason: this.dataItem.noBackupReason,
        },
      }),
    );
  }

  _noBackupReasonChanged(value) {
    this.dataItem = {
      ...this.dataItem,
      noBackupReason: value,
    };
    this._dispatchDataItemChanged();
    this.dispatchEvent(
      new CustomEvent<BackupChange>('backup-change', {
        composed: true,
        bubbles: true,
        detail: {
          uuid: this.dataItem.uuid,
          backupFrequency: this.dataItem.backupFrequency,
          backupResponsible: this.dataItem.backupResponsible,
          noBackupReason: this.dataItem.noBackupReason,
        },
      }),
    );
  }

  _dispatchDataItemChanged() {
    this.dispatchEvent(
      new CustomEvent('data-item-changed', {
        bubbles: true,
        composed: true,
        detail: this.dataItem,
      }),
    );
  }

  render() {
    return html`
      <d-wrap>
        <d-select-dropdown
          theme-page
          small-label
          semibold-label
          label="${t('computers.backupHowOften')}"
          placeholder="${t('computers.backupChooseFrequency')}"
          .options=${backupFrequencyOptions}
          .value=${this.dataItem.backupFrequency}
          @value-changed=${(e) => this._frequencyChanged(e.detail.value)}
        ></d-select-dropdown>
        ${this.dataItem.backupFrequency !== '' && this.dataItem.backupFrequency !== 'never'
          ? html`
              <d-select-dropdown
                theme-page
                small-label
                semibold-label
                label="${t('computers.backupByWhom')}"
                placeholder="${t('computers.backupChooseResponsible')}"
                .options=${this.responsibleOptions}
                .value=${this.dataItem.backupResponsible}
                @value-changed=${(e) => this._responsibleChanged(e.detail.value)}
              ></d-select-dropdown>
            `
          : nothing}
        ${this.dataItem.backupFrequency === 'never'
          ? html`
              <d-select-dropdown
                theme-page
                small-label
                semibold-label
                label="${t('computers.backupWhyNotQuestion')}"
                placeholder="${t('computers.backupChooseReason')}"
                .options=${noBackupReasonOptions}
                .value=${this.dataItem.noBackupReason}
                @value-changed=${(e) => this._noBackupReasonChanged(e.detail.value)}
              ></d-select-dropdown>
            `
          : nothing}
      </d-wrap>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-edit-backup': DEditBackup;
  }
}
